import { useCallback } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { useRouter } from 'next/router';

import {
  ContentType as ModalContentType,
  activeContent as activeModalContent,
  emailVerificationDetails,
  openAuthFlowModal,
  openEmailLoginModal,
  openEmailTokenSentModal,
  openSignupModal,
  setCurrentUrl,
} from '../context/auth-flow';
import { useAuthContext } from '@restworld/feature-authentication';

export const useLoginSignupPopup = () => {
  const router = useRouter();
  const onOpenEmailLoginModal = useSetAtom(openEmailLoginModal);
  const onOpenEmailTokenSentModal = useSetAtom(openEmailTokenSentModal);
  const onOpenSignupModal = useSetAtom(openSignupModal);
  const onSetCurrentUrl = useSetAtom(setCurrentUrl);
  const setContent = useSetAtom(activeModalContent);
  const openModal = useSetAtom(openAuthFlowModal);
  const emailDetails = useAtomValue(emailVerificationDetails);
  const { logout } = useAuthContext();
  const currentUrl = router?.asPath;

  const onStartLogin = useCallback(() => {
    openModal();
    onSetCurrentUrl(currentUrl);
    // Resume the code step if a still-valid code was already sent earlier this
    // session (the atom is in-memory, so a page refresh clears it and we start
    // fresh at email entry).
    const hasPendingCode =
      !!emailDetails.email && !!emailDetails.tokenExpiresAt && emailDetails.tokenExpiresAt > Date.now();
    if (hasPendingCode) {
      setContent(ModalContentType.EMAIL_SENT);
      onOpenEmailTokenSentModal();
    } else {
      setContent(ModalContentType.EMAIL_LOGIN);
      onOpenEmailLoginModal();
    }
  }, [
    emailDetails.email,
    emailDetails.tokenExpiresAt,
    onOpenEmailLoginModal,
    onOpenEmailTokenSentModal,
    onSetCurrentUrl,
    openModal,
    currentUrl,
    setContent,
  ]);

  const onStartSignup = useCallback(() => {
    setContent(ModalContentType.SIGNUP);
    openModal();
    onOpenSignupModal();
    onSetCurrentUrl(currentUrl);
  }, [onOpenSignupModal, onSetCurrentUrl, openModal, currentUrl, setContent]);
  const onLogout = () => {
    if (logout) {
      logout();
    }
  };
  return {
    onStartLogin,
    onStartSignup,
    onLogout,
  };
};
